import type {Fn} from './types';

type Task = () => void;
type CancelSchedule = () => void;
interface ScheduleOptions {
    delay?: number;
    signal?: AbortSignal | TaskSignal;
    /**
     * Task Priority
     * If the options.priority parameter is set then the task priority cannot
     * be changed, and any priority on the signal is ignored. Otherwise, if the
     * signal is a TaskSignal its priority is used to set the initial task
     * priority, and the signal's controller may later use it to change the
     * task priority.
     *
     * @default 'user-visible'
     * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/Prioritized_Task_Scheduling_API#task_priorities}
     */
    priority?: 'user-blocking' | 'user-visible' | 'background';
}
type ScheduleFn = <T extends unknown[], R>(
    task: Fn<T, R>,
    options: ScheduleOptions,
) => CancelSchedule;

interface Scheduler {
    /**
     * Schedule a task to run
     */
    schedule(task: Task): void;
    /**
     * Run the scheduled task immediately
     */
    run(): void;
    /**
     * Cancel the scheduled task
     */
    cancel(): void;
}

/**
 * Fallback implementation of Task Scheduling API using `setTimeout`
 */
export const scheduleWithTimeout: ScheduleFn = (task, options) => {
    if (options.signal?.aborted) {
        return () => {
            // No op as the given signal is already aborted
        };
    }
    const timeoutId = window.setTimeout(task, options.delay);
    const cancelSchedule = () => {
        window.clearTimeout(timeoutId);
        options.signal?.removeEventListener('abort', cancelSchedule);
    };
    options.signal?.addEventListener('abort', cancelSchedule);
    return cancelSchedule;
};

/**
 * Prioritized Task Scheduling API
 * @see {@link https://wicg.github.io/scheduling-apis/}
 */
export const scheduleWithScheduler: ScheduleFn = (task, options) => {
    if ('scheduler' in globalThis) {
        if (!options.signal) {
            const taskController = new TaskController();
            scheduler
                .postTask(task, {
                    ...options,
                    signal: taskController.signal,
                })
                .catch(error => {
                    if (error.name !== 'AbortError') {
                        throw error;
                    }
                });
            return () => {
                taskController.abort();
            };
        }
        if (!options.signal.aborted) {
            scheduler.postTask(task, options);
        }
        return () => {
            // No Op as the task is controlled via the given signal
        };
    }
    return scheduleWithTimeout(task, options);
};

export const createScheduler = (
    options: ScheduleOptions = {},
    scheduleTask: ScheduleFn = scheduleWithTimeout,
): Scheduler => {
    const noop = () => {
        // No op;
    };
    let cb = noop;
    let cancelSchedule = noop;
    const scheduler = Object.freeze({
        schedule(task: () => void) {
            if (options.signal?.aborted) {
                return;
            }
            cb = task;
            cancelSchedule();
            const handleTask = () => {
                if (cb === noop) {
                    return;
                }
                cb();
                cb = noop;
                cancelSchedule = noop;
                options.signal?.removeEventListener('abort', scheduler.cancel);
            };
            cancelSchedule = scheduleTask(handleTask, options);
        },
        run() {
            cancelSchedule();
            options.signal?.removeEventListener('abort', scheduler.cancel);
            if (cb === noop) {
                return;
            }
            cb();
            cb = noop;
        },
        cancel() {
            cancelSchedule();
            cancelSchedule = noop;
            cb = noop;
            options.signal?.removeEventListener('abort', scheduler.cancel);
        },
    });
    options.signal?.addEventListener('abort', scheduler.cancel);
    return scheduler;
};
